<script setup lang="ts">
import { OrderBy } from '@/types/shared/filters'
import type { IEquipmentFilters } from '@/types/hvl/filters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { reactive, ref } from 'vue'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { formatISODateToDateString } from '@/util/formatters'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import { SiteType } from '@/types/core/Site'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useEquipment } from '@/composables/hvl/equipment'
import { useOIDCStore } from '@/stores/oidc.store'
import { storeToRefs } from 'pinia'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import type { IEquipmentType } from '@/types/hvl/EquipmentType'
import type { IEquipment } from '@/types/hvl/Equipment'
import UpsertEquipmentDialog from '@/components/hvl/UpsertEquipmentDialog.vue'
import HvlEquipmentTypeSelect from '@/components/hvl/common/HvlEquipmentTypeSelect.vue'

const oidcStore = useOIDCStore()
const { isHVLManagerUser } = storeToRefs(oidcStore)

const { getEquipmentQuery, deleteEquipmentMutation } = useEquipment()

const upsertDialogVisible = ref<boolean>(false)
const equipmentBeingModified = ref<IEquipment>()

const filters = reactive<IEquipmentFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  name: undefined,
  location: undefined,
  site_id: undefined,
  type_id: undefined,
})

const { isPending, isError, isRefetching, data, error, refetch } = getEquipmentQuery(filters)

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const locationSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Location',
  onChange: (val: string) => {
    if (val == '') {
      filters.location = undefined
    } else {
      filters.location = val
    }
  },
}

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id ?? undefined
}

const onEquipmentTypeFilterUpdate = (value?: IEquipmentType) => {
  filters.type_id = value?.id ?? undefined
}

const onEdit = (equipment: IEquipment) => {
  equipmentBeingModified.value = equipment
  upsertDialogVisible.value = true
}
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button v-if="isHVLManagerUser" type="primary" :disabled="isPending" @click="upsertDialogVisible = true"
        >Create</el-button
      >
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex inline-flex gap-4">
        <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
        <TableSearchFilter :search-settings="locationSearchSettings" :disabled="isPending" />
        <RcSiteSelect
          placeholder="Site ID or Name"
          :type="SiteType.DataCenter"
          :disabled="isPending"
          style="width: 200px"
          @update:model-value="onSiteFilterUpdate"
        />
        <HvlEquipmentTypeSelect
          placeholder="Equipment Type"
          :disabled="isPending"
          style="width: 200px"
          @update:model-value="onEquipmentTypeFilterUpdate"
        />
      </div>
      <el-table v-loading="isPending" :data="data?.data" empty-text="No data" stripe>
        <el-table-column prop="name" label="Name" :min-width="224" />
        <el-table-column prop="type.name" label="Type" :min-width="224" />
        <el-table-column prop="site.name" label="Site" :min-width="224" />
        <el-table-column prop="location" label="Location" :min-width="224" />
        <el-table-column label="Last Updated" :min-width="220">
          <template #default="scope">
            {{
              formatISODateToDateString(
                scope.row.updated_at ? scope.row.updated_at : scope.row.created_at,
                'MM/dd/yyyy HH:mm:ss ZZZZ'
              )
            }}
          </template>
        </el-table-column>
        <el-table-column :min-width="70" align="right">
          <template #default="scope">
            <el-button v-if="isHVLManagerUser" link title="Edit" @click="onEdit(scope.row as IEquipment)">
              <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
            </el-button>
            <el-button
              v-if="isHVLManagerUser"
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Equipment',
                  'Are you sure you want to delete this equipment? This action cannot be undone',
                  scope.row.id,
                  deleteEquipmentMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:page-size="filters.per_page"
        v-model:current-page="filters.page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isPending"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <UpsertEquipmentDialog
        v-model="upsertDialogVisible"
        :equipment="equipmentBeingModified"
        @close="equipmentBeingModified = undefined"
      />
    </template>
  </ViewLayout>
</template>
